import React, {useEffect, useState} from 'react';
import {Box, Collapse, Divider, Link, Stack, Tooltip, Typography} from "@mui/material"
import {KeyboardArrowRight, OpenInNew} from "@mui/icons-material"
import {Link as RouterLink} from "react-router-dom"
import {AnyCustomModelJson, resolveBaseModelName} from "../../api/json/CustomModelJson"
import {ModelPlatform, resolveNameForModelPlatform} from "../../api/enum/ModelPlatform"
import {TemplateType} from "../../api/enum/TemplateType"
import {EndpointJson} from "../../api/json/EndpointJson"
import {EndpointStatus, resolveEndpointStatusColor, resolveEndpointStatusLabel} from "../../api/enum/EndpointStatus"
import {CustomModelRequests} from "../../api/requests/CustomModelRequests"
import {TimeUtil} from "../../library/TimeUtil"
import SpecRow from "../common/SpecRow"
import ClassChip from "../common/ClassChip"
import CustomModelStatusInfo from "./CustomModelStatusInfo"

const TOGETHER_ENDPOINTS_URL = "https://api.together.ai/endpoints"
const POLL_INTERVAL_MS = 6000

const sectionLabelSx = {display: "block", fontWeight: 700, color: "text.secondary"} as const
const subtleLinkSx = {color: "text.secondary", textDecorationColor: "currentColor"} as const

function formatTrainingType(type?: string | null) {
    if (!type) return type
    return type.toUpperCase() === "LORA" ? "LoRA" : type
}

function formatLearningRate(lr: number | string | null | undefined): React.ReactNode {
    if (lr == null || lr === "") return null
    const n = Number(lr)
    if (!isFinite(n)) return String(lr)
    return <>{lr} <Box component="span" sx={{color: "text.secondary"}}>({n.toExponential()})</Box></>
}

function CustomModelDetailRail({customModel, projectId}: {customModel: AnyCustomModelJson, projectId: string}) {
    const [endpoints, setEndpoints] = useState<EndpointJson[]>()
    const [showIdentifiers, setShowIdentifiers] = useState(false)

    const isTogether = customModel.platform === ModelPlatform.TOGETHER
    const showEndpoint = isTogether && !!customModel.completed

    useEffect(() => {
        if (!showEndpoint) return

        let active = true
        const load = () => {
            CustomModelRequests.endpoints(customModel.id)
                .then(result => { if (active) setEndpoints(result) })
                .catch(() => {})
        }
        load()
        const interval = setInterval(load, POLL_INTERVAL_MS)
        return () => {
            active = false
            clearInterval(interval)
        }
    }, [customModel.id, showEndpoint])

    const trained = customModel.completed || customModel.failed

    let trainingTimeLabel: string | null = null
    if (customModel.completed) {
        const minutes = Math.round((Date.parse(customModel.completed) - Date.parse(customModel.created)) / (1000 * 60))
        trainingTimeLabel = minutes >= 90 ? `${Math.round(minutes / 120) * 2} hours` : `${minutes} minutes`
    }
    const createdDate = new Date(customModel.created).toLocaleDateString("en-US", {day: "numeric", month: "long", year: "numeric"})
    const trainedSummary = [
        customModel.trainingRowCount != null ? `${customModel.trainingRowCount.toLocaleString()} rows` : null,
        trainingTimeLabel,
    ].filter(Boolean).join(" · ")

    const endpoint = endpoints?.find(it => it.status !== EndpointStatus.DELETED)

    const loraSummary = customModel.platform === ModelPlatform.TOGETHER
        ? [
            customModel.loraRank != null ? `r${customModel.loraRank}` : null,
            customModel.loraAlpha != null ? `α${customModel.loraAlpha}` : null,
        ].filter(Boolean).join(" · ")
        : ""

    const template = customModel.template
    const templateInput = template.type === TemplateType.CHAT ? template.user : template.prompt
    const templateOutput = template.type === TemplateType.CHAT ? template.assistant : template.completion
    const templateSystem = template.type === TemplateType.CHAT ? template.system : ""

    return (
        <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 2.5}}>
            <Stack spacing={2.5} divider={<Divider flexItem/>}>
                <Stack spacing={1}>
                    <Typography variant="overline" sx={sectionLabelSx}>Model</Typography>
                    <SpecRow label="Status" value={<CustomModelStatusInfo customModel={customModel} horizontalLayout/>}/>
                    <SpecRow label="Platform" value={<ClassChip value={resolveNameForModelPlatform(customModel.platform)}/>}/>
                    <SpecRow label="Base model" value={resolveBaseModelName(customModel)}/>
                    {trained &&
                        <>
                            <SpecRow label="Created" value={createdDate}/>
                            {trainedSummary && <SpecRow label="Trained on" value={trainedSummary}/>}
                        </>
                    }
                </Stack>

                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="overline" sx={sectionLabelSx}>Template</Typography>
                        <Link component={RouterLink} to={`/projects/${projectId}/templates/${template.id}`}
                              variant="caption" sx={subtleLinkSx}>
                            {template.name}
                        </Link>
                    </Stack>
                    <Box sx={{fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-word"}}>
                        {templateInput} <Box component="span" sx={{color: "text.secondary"}}>→</Box> {templateOutput}
                    </Box>
                    {templateSystem.trim()
                        ? <Tooltip title={<Box sx={{whiteSpace: "pre-wrap"}}>{templateSystem.slice(0, 600)}</Box>}>
                            <Typography variant="caption" color="text.secondary"
                                        sx={{cursor: "help", borderBottom: "1px dotted", alignSelf: "flex-start"}}>
                                System prompt · {templateSystem.length} chars
                            </Typography>
                        </Tooltip>
                        : <Typography variant="caption" color="text.secondary">No system prompt</Typography>
                    }
                </Stack>

                <Stack spacing={1}>
                    <Typography variant="overline" sx={sectionLabelSx}>Training</Typography>
                    {customModel.platform === ModelPlatform.OPEN_AI &&
                        <>
                            <SpecRow label="Epochs" value={customModel.numberOfEpochs}/>
                            {customModel.learningRateMultiplier != null &&
                                <SpecRow label="LR multiplier" value={customModel.learningRateMultiplier}/>
                            }
                            {customModel.promptLossWeight != null && <SpecRow label="Loss weight" value={customModel.promptLossWeight}/>}
                            {customModel.batchSize != null && <SpecRow label="Batch size" value={customModel.batchSize}/>}
                        </>
                    }
                    {customModel.platform === ModelPlatform.TOGETHER &&
                        <>
                            <SpecRow label="Type" value={formatTrainingType(customModel.trainingType)}/>
                            <SpecRow label="Epochs" value={customModel.numberOfEpochs}/>
                            {customModel.learningRate != null &&
                                <SpecRow label="Learning rate" value={formatLearningRate(customModel.learningRate)}/>
                            }
                            {loraSummary && <SpecRow label="LoRA" value={loraSummary}/>}
                            {customModel.batchSize != null && <SpecRow label="Batch size" value={customModel.batchSize}/>}
                        </>
                    }
                </Stack>

                {showEndpoint &&
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="overline" sx={sectionLabelSx}>Endpoint</Typography>
                            {endpoint &&
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: `${resolveEndpointStatusColor(endpoint.status)}.main`}}/>
                                    <Typography variant="caption" color="text.secondary">{resolveEndpointStatusLabel(endpoint.status)}</Typography>
                                </Stack>
                            }
                        </Stack>
                        {endpoint &&
                            <>
                                <SpecRow label="Hardware" value={endpoint.hardware}/>
                                <SpecRow label="Last used" value={TimeUtil.ago(endpoint.lastUsedAt)}/>
                            </>
                        }
                        {endpoints !== undefined && !endpoint &&
                            <Typography variant="body2" color="text.secondary">
                                No endpoint running. One deploys automatically when the model is used.
                            </Typography>
                        }
                        <Link href={TOGETHER_ENDPOINTS_URL} target="_blank" rel="noopener"
                              variant="body2" sx={{...subtleLinkSx, display: "inline-flex", alignItems: "center", gap: 0.5}}>
                            Manage on Together <OpenInNew fontSize="inherit"/>
                        </Link>
                    </Stack>
                }

                {trained &&
                    <Box>
                        <Link component="button"
                              type="button"
                              underline="none"
                              color="text.secondary"
                              onClick={() => setShowIdentifiers(value => !value)}
                              sx={{display: "inline-flex", alignItems: "center"}}>
                            <KeyboardArrowRight fontSize="small"
                                                sx={{transition: "transform 0.15s", transform: showIdentifiers ? "rotate(90deg)" : "none"}}/>
                            <Typography variant="overline" sx={sectionLabelSx}>Identifiers</Typography>
                        </Link>
                        <Collapse in={showIdentifiers}>
                            <Stack spacing={1.5} sx={{mt: 1}}>
                                {customModel.platform === ModelPlatform.TOGETHER &&
                                    <>
                                        {customModel.outputModelName &&
                                            <SpecRow label="Output model name" value={customModel.outputModelName} mono stacked/>
                                        }
                                        {customModel.trainingFileId &&
                                            <SpecRow label="Training file ID" value={customModel.trainingFileId} mono stacked/>
                                        }
                                        {customModel.validationFileId &&
                                            <SpecRow label="Validation file ID" value={customModel.validationFileId} mono stacked/>
                                        }
                                    </>
                                }
                                {customModel.platformId &&
                                    <SpecRow label={isTogether ? "Together ID" : "Provider ID"} value={customModel.platformId} mono stacked/>
                                }
                            </Stack>
                        </Collapse>
                    </Box>
                }
            </Stack>
        </Box>
    )
}

export default CustomModelDetailRail;
